import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LocalDate } from 'src/utilities/local-date';
import '../../library/elements/d-label';
import '../../library/editors/elements/d-select-radio';
import '../../library/editors/elements/d-select-date';
import '../../library/editors/elements/d-select-dropdown';
import '../../library/editors/elements/d-select-tag';
import '../../library/editors/components/d-edit-time';
import '../../library/elements/d-wrap';
import '../../library/fields/d-expansion';
import '../../library/elements/d-action';
import '../../library/components/d-calendar-year';
import { endYear, RecurrenceRule } from 'src/store';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import { t } from 'src/i18n';

@customElement('d-create-occurrences')
export class DCreateOccurrences extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      position: relative;
    }
    .bottom {
      margin-top: 10px;
    }
    d-label {
      margin-top: 9px;
      margin-right: 12px;
    }
    .between {
      display: flex;
      align-items: center;
    }
    #weekdays {
      margin-bottom: -4px;
    }
  `;
  @property({ type: Boolean })
  regular = false;
  @property({ type: String })
  modelDateTime = '';
  @property({ type: String })
  today = LocalDate.now().toString();
  @property({ type: String })
  startDate = '';
  @property({ type: String })
  time = 'NONE';
  @property({ type: Number })
  duration = 0;
  @property({ type: String })
  freq = 'MONTHLY';
  @property({ type: Number })
  interval = 1;
  @property({ type: String })
  recurrenceType: 'BYMONTHDAY' | 'BYDAY' = 'BYMONTHDAY';
  @property({ type: Array })
  weekdays: string[] = [];
  @property({ type: String })
  endDate = endYear + '-12-31';
  @property({ type: Number })
  contentStickyTop = 0;

  private get freqInterval(): string {
    return this.freq + '-' + this.interval;
  }

  private get frequencyOptions(): SelectDropdownOption[] {
    return [
      { value: 'DAILY-1', text: t('events.frequencyOptions.daily1') },
      { value: 'WEEKLY-1', text: t('events.frequencyOptions.weekly1') },
      { value: 'WEEKLY-2', text: t('events.frequencyOptions.weekly2') },
      { value: 'WEEKLY-3', text: t('events.frequencyOptions.weekly3') },
      { value: 'WEEKLY-4', text: t('events.frequencyOptions.weekly4') },
      { value: 'WEEKLY-5', text: t('events.frequencyOptions.weekly5') },
      { value: 'WEEKLY-6', text: t('events.frequencyOptions.weekly6') },
      { value: 'MONTHLY-1', text: t('events.frequencyOptions.monthly1') },
      { value: 'MONTHLY-2', text: t('events.frequencyOptions.monthly2') },
      { value: 'MONTHLY-3', text: t('events.frequencyOptions.monthly3') },
      { value: 'MONTHLY-4', text: t('events.frequencyOptions.monthly4') },
      { value: 'MONTHLY-5', text: t('events.frequencyOptions.monthly5') },
      { value: 'MONTHLY-6', text: t('events.frequencyOptions.monthly6') },
      { value: 'YEARLY-1', text: t('events.frequencyOptions.yearly1') },
      { value: 'YEARLY-2', text: t('events.frequencyOptions.yearly2') },
      { value: 'YEARLY-3', text: t('events.frequencyOptions.yearly3') },
      { value: 'YEARLY-4', text: t('events.frequencyOptions.yearly4') },
      { value: 'YEARLY-5', text: t('events.frequencyOptions.yearly5') },
      { value: 'YEARLY-6', text: t('events.frequencyOptions.yearly6') },
    ];
  }

  private readonly weekdayList: { id: string; value: string }[] = [
    { id: 'MO', value: 'M' },
    { id: 'TU', value: 'T' },
    { id: 'WE', value: 'O' },
    { id: 'TH', value: 'T' },
    { id: 'FR', value: 'F' },
    { id: 'SA', value: 'L' },
    { id: 'SU', value: 'S' },
  ];

  private get referenceDate() {
    if (this.modelDateTime) {
      return this.modelDateTime.split(' ')[0];
    }
    return this.today;
  }

  private get byMonthdayOption() {
    const day = LocalDate.fromString(this.startDate).day();
    if (this.freq === 'YEARLY') {
      const month = LocalDate.fromString(this.startDate).nameOfMonth();
      return t('events.byMonthdayOptionYearly', { day, month });
    }
    return t('events.byMonthdayOptionMonthly', { day });
  }

  private get byDayOption() {
    const weekday = t('events.weekdayDefinite', {
      weekday: LocalDate.fromString(this.referenceDate).getDayOfWeekString(),
    });
    const date = LocalDate.fromString(this.referenceDate).day();
    const month = LocalDate.fromString(this.referenceDate).nameOfMonth();
    let ordinal = t('events.ordinals.first');
    if (date > 7 && date < 15) {
      ordinal = t('events.ordinals.second');
    }
    if (date > 14 && date < 22) {
      ordinal = t('events.ordinals.third');
    }
    if (date > 21 && date < 29) {
      ordinal = t('events.ordinals.fourth');
    }
    if (LocalDate.fromString(this.referenceDate).daysInMonth() - date < 7) {
      ordinal = t('events.ordinals.last');
    }
    if (this.freq === 'YEARLY') {
      return t('events.byDayOptionYearly', { ordinal, weekday, month });
    }
    return t('events.byDayOptionMonthly', { ordinal, weekday });
  }

  private get recurrenceTypeOptions(): SelectDropdownOption[] {
    return [
      { value: 'BYMONTHDAY', text: this.byMonthdayOption },
      { value: 'BYDAY', text: this.byDayOption },
    ];
  }

  private get weekdayOptions(): SelectDropdownOption[] {
    const weekdayIndex = LocalDate.fromString(this.startDate).dayOfWeek().valueOf();
    return this.weekdayList.map((o, index) => {
      return { value: o.id, text: o.value, disabled: weekdayIndex === index };
    });
  }

  private get weekdaysValue(): string[] {
    const startDateDay = LocalDate.fromString(this.startDate).dayOfWeek();
    return this.weekdayList
      .filter((w, index) => {
        return this.weekdays.includes(w.id) || index === startDateDay;
      })
      .map((w) => {
        return w.id;
      });
  }

  private getByDayDate(referenceDate: string, monthsCount: number) {
    const date = LocalDate.fromString(referenceDate);
    const dayOfWeek = date.dayOfWeek();
    const dateNumber = date.day();
    // Get date for the first day in result month
    const referenceDateParts = date.toString().split('-');
    const referenceDateFirstOfMonth = LocalDate.fromString(
      referenceDateParts[0] + '-' + referenceDateParts[1] + '-' + '01',
    );
    const resultMonthFirstDay = referenceDateFirstOfMonth.plusMonths(monthsCount);
    // Get weekday of the first day in result month
    const resultMonthFirstDayWeekDay = resultMonthFirstDay.dayOfWeek();
    // Get date for first occurrence of correct weekday
    let difference = dayOfWeek - resultMonthFirstDayWeekDay;
    if (difference < 0) {
      difference += 7;
    }
    const firstWeekDayNewMonth = resultMonthFirstDay.plusDays(difference);
    // If the reference date is the last occurrence of this weekday in its month,
    // find the last occurrence in the result month (mirrors weekdayInstanceNumber logic)
    if (date.daysInMonth() - dateNumber < 7) {
      let resultDate = firstWeekDayNewMonth;
      while (resultDate.plusWeeks(1).month() === resultMonthFirstDay.month()) {
        resultDate = resultDate.plusWeeks(1);
      }
      return resultDate.toString();
    }
    // Get occurrence number for weekday in month
    const occurrence = Math.ceil(dateNumber / 7);
    // Add number of weeks needed to get correct occurrence number
    const resultDate = firstWeekDayNewMonth.plusWeeks(occurrence - 1);
    return resultDate.toString();
  }

  private setStartDate() {
    if (this.freq === 'DAILY') {
      this.startDate = LocalDate.fromString(this.referenceDate).plusDays(1).toString();
    }
    if (this.freq === 'WEEKLY') {
      this.startDate = LocalDate.fromString(this.referenceDate).plusWeeks(this.interval).toString();
    }
    if (this.freq === 'MONTHLY') {
      if (this.recurrenceType === 'BYMONTHDAY') {
        this.startDate = LocalDate.fromString(this.referenceDate).plusMonths(this.interval).toString();
      }
      if (this.recurrenceType === 'BYDAY') {
        this.startDate = this.getByDayDate(this.referenceDate, this.interval);
      }
    }
    if (this.freq === 'YEARLY') {
      if (this.recurrenceType === 'BYMONTHDAY') {
        this.startDate = LocalDate.fromString(this.referenceDate).plusYears(this.interval).toString();
      }
      if (this.recurrenceType === 'BYDAY') {
        this.startDate = this.getByDayDate(this.referenceDate, this.interval * 12);
      }
    }
  }

  private onFreqIntervalChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    const freqInterval = e.detail.value;
    this.freq = freqInterval.split('-')[0];
    this.interval = Number(freqInterval.split('-')[1]);
    this.setStartDate();
  }

  private onWeekdaysChanged(e: CustomEvent<{ value: string[] }>) {
    e.stopPropagation();
    this.weekdays = e.detail.value;
  }

  private onRecurrenceTypeChanged(e: CustomEvent<{ value: 'BYMONTHDAY' | 'BYDAY' }>) {
    e.stopPropagation();
    this.recurrenceType = e.detail.value;
    this.setStartDate();
  }

  private onStartDateChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.startDate = e.detail.value;
  }

  private onEndDateChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.endDate = e.detail.value;
  }

  private onTimeChanged(e: CustomEvent<{ time: string; durationMinutes: number }>) {
    e.stopPropagation();
    this.time = e.detail.time;
    this.duration = e.detail.durationMinutes;
  }

  private weekdayInstanceNumber(date: string) {
    const localDate = LocalDate.fromString(date);
    const dayOfMonth = localDate.day();
    if (localDate.daysInMonth() - dayOfMonth < 7) {
      return -1;
    }
    return Math.ceil(dayOfMonth / 7);
  }

  private get recurrence(): string | undefined {
    if (this.regular) {
      let result = 'FREQ=' + this.freq;
      if (this.freq !== 'DAILY') {
        if (this.freq === 'WEEKLY') {
          result += ';INTERVAL=' + this.interval + ';BYDAY=' + this.weekdaysValue.join(',');
        } else {
          result += ';INTERVAL=' + this.interval + ';' + this.recurrenceType + '=';
          if (this.recurrenceType === 'BYMONTHDAY') {
            result += LocalDate.fromString(this.startDate).day();
          } else if (this.recurrenceType === 'BYDAY') {
            result +=
              this.weekdayList[LocalDate.fromString(this.referenceDate).dayOfWeek()].id +
              ';BYSETPOS=' +
              this.weekdayInstanceNumber(this.referenceDate);
          }
        }
        if (this.freq === 'YEARLY') {
          result += ';BYMONTH=' + LocalDate.fromString(this.startDate).month();
        }
      }
      result += ';UNTIL=' + this.endDate.replaceAll('-', '');
      return result;
    }
  }

  private get occurrences(): string[] {
    if (this.recurrence) {
      const occurrencesFromRule: string[] = [];
      const r = RecurrenceRule.fromString('DTSTART=' + this.startDate.replaceAll('-', '') + ';' + this.recurrence);
      const start = LocalDate.fromString('1000-01-01');
      const end = LocalDate.fromString(endYear + '-12-31');
      let x = r.sameOrAfter(start);
      while (x !== null && x.isSameOrBefore(end)) {
        const date = x.toString();
        occurrencesFromRule.push(date);
        x = r.after(x);
      }
      return occurrencesFromRule.map((o) => {
        let dateTime = o;
        if (this.time !== 'NONE') {
          dateTime += ' ' + this.time + ' ' + this.duration;
        }
        return dateTime;
      });
    }
    let time = '';
    if (this.time !== 'NONE') {
      time = ' ' + this.time + ' ' + this.duration;
    }
    return [this.startDate + time];
  }

  private cancel() {
    this.dispatchEvent(
      new CustomEvent('cancel', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private done() {
    this.dispatchEvent(
      new CustomEvent('done', {
        bubbles: true,
        composed: true,
        detail: { value: this.occurrences },
      }),
    );
  }

  renderRecurrenceSettings() {
    return html`
      <d-wrap>
        <d-select-dropdown
          controller
          .value=${this.freqInterval}
          .options=${this.frequencyOptions}
          @value-changed=${(e) => this.onFreqIntervalChanged(e)}
        ></d-select-dropdown>
        ${this.freq === 'WEEKLY'
          ? html` <div class="between">${t('events.occurrencesOnWeekday')}</div>
              <d-select-tag
                controller
                id="weekdays"
                .options=${this.weekdayOptions}
                .value=${this.weekdaysValue}
                @value-changed=${this.onWeekdaysChanged}
              >
              </d-select-tag>`
          : nothing}
        ${this.freq === 'MONTHLY' || this.freq === 'YEARLY'
          ? html`<d-select-dropdown
              controller
              .options=${this.recurrenceTypeOptions}
              .value=${this.recurrenceType}
              @value-changed=${this.onRecurrenceTypeChanged}
            >
            </d-select-dropdown>`
          : nothing}
      </d-wrap>
    `;
  }

  renderDaySettings() {
    return html`
      <d-wrap>
        ${this.regular
          ? html`
              <d-edit-time
                controller
                whole-day-option
                .time=${this.time}
                .durationMinutes=${this.duration}
                @value-changed=${this.onTimeChanged}
              ></d-edit-time>
              <d-select-date
                label=${t('periods.to')}
                inline-label
                light-label
                controller
                .disableAfter=${endYear + '-12-31'}
                .value=${this.endDate}
                @value-changed=${(e) => this.onEndDateChanged(e)}
              ></d-select-date>
            `
          : html`
              <d-select-date
                inline-label
                light-label
                controller
                .disableBefore=${LocalDate.fromString(this.referenceDate).plusDays(1).toString()}
                .disableAfter=${endYear + '-12-31'}
                .value=${this.startDate}
                @value-changed=${(e) => this.onStartDateChanged(e)}
              ></d-select-date>
              <d-edit-time
                controller
                whole-day-option
                .time=${this.time}
                .durationMinutes=${this.duration}
                @value-changed=${this.onTimeChanged}
              ></d-edit-time>
            `}
      </d-wrap>
    `;
  }

  renderTopPart() {
    if (this.regular) {
      return this.renderRecurrenceSettings();
    }
    return this.renderDaySettings();
  }

  renderBottomPart() {
    if (this.regular) {
      return html`<div class="bottom">${this.renderDaySettings()}</div>`;
    }
    return nothing;
  }

  render() {
    return html`
      <d-wrap split-reverse baseline tight>
        <div>
          <d-action plain @click=${() => this.cancel()}>${t('common:cancel')}</d-action>
          <d-action plain @click=${() => this.done()}>${t('events.occurrencesAdd')}</d-action>
        </div>
        ${this.renderTopPart()}
      </d-wrap>
      ${this.renderBottomPart()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-create-occurrences': DCreateOccurrences;
  }
}
